import { ExpandableIntro } from "@/components/expandable-intro";
import { Link, createFileRoute } from "@tanstack/react-router";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks } from "@/components/eeat-blocks";
import { KeyTakeaways } from "@/components/key-takeaways";
import { OffshoreLicenses } from "@/components/offshore-licenses";
import { CruksStatsInfographic } from "@/components/cruks-stats-infographic";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/zonder-cruks";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImage, ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/casino-zonder-cruks";
const URL = `${SITE.url}${PATH}/`;
const TITLE = "Casino zonder Cruks 2026: beste online casino's & complete gids";

export const Route = createFileRoute("/casino-zonder-cruks")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: guide.description },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("zonder-cruks"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            datePublished: "2026-11-04",
            dateModified: "2026-11-04",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role, url: `${SITE.url}/auteurs/${author.slug}/` }
              : { "@type": "Organization", name: SITE.name },
            image: ogImage("zonder-cruks"),
            about: [
              { "@type": "Thing", name: "Cruks", sameAs: "https://www.cruks.nl" },
              { "@type": "Thing", name: "Online casino" },
            ],
            citation: [
              "https://open.overheid.nl/documenten/77025de3-b7e9-4cd0-b7b2-09913b79d3e8/",
              "https://kansspelautoriteit.nl/",
              "https://www.cruks.nl",
            ],
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role, url: `${SITE.url}/auteurs/${reviewer.slug}/` }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Top 10 online casino's zonder Cruks – november 2026",
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            numberOfItems: getPublicCasinos().length,
            itemListElement: getPublicCasinos().map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: c.name,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Casino zonder Cruks", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: ZonderCruksPage,
});

function ZonderCruksPage() {
  const casinos = getPublicCasinos();
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "Een casino zonder Cruks heeft nooit een Nederlandse vergunning van de Kansspelautoriteit.",
    "Zonder vergunning geen toezicht op uitbetalingen en geen klachtenprocedure.",
    "Een Curaçao- of andere buitenlandse licentie is niet geldig voor Nederlandse spelers.",
    "Sta je in Cruks? De inschrijving is een bescherming; Loket Kansspel helpt gratis en anoniem.",
    "Snel spelen en uitbetalen kan prima legaal, met iDEAL of Trustly.",
    "Alle casino's in onze toplist zijn vergund en controleren het Cruks-register.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Casino zonder Cruks</span>
      </nav>

      <h1 className="max-w-6xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        Beste online casino&apos;s zonder Cruks in 2026 – onze toplijst
      </h1>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>
        Zoek je een casino zonder Cruks? Hieronder vind je de beste online casino&apos;s die
        wij vergelijken op uitbetalingssnelheid, limieten en
        betaalmethoden. Daarna lees je wat een casino zonder Cruks echt betekent, welke
        risico&apos;s je loopt en waar je hulp vindt als je zelf in Cruks staat.
      </ExpandableIntro>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              Top 10 online casino&apos;s zonder Cruks – november 2026
            </h2>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
          </section>

          <div className="mt-10">
            <KeyTakeaways items={takeaways} />
          </div>

          <section className="mt-12" aria-label="Alles over casino's zonder Cruks">
            <ArticleBody
              blocks={guide.blocks}
              currentPath={PATH}
              budget={budget}
              slots={{ stats: <CruksStatsInfographic />, licenses: <OffshoreLicenses /> }}
            />
          </section>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="Cruks en legale casino's"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over casino&apos;s zonder Cruks
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
